/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState, useEffect, useMemo, Fragment } from 'react';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Check2, X } from 'react-bootstrap-icons';
import classnames from 'classnames';
import each from 'lodash/each';
import filter from 'lodash/filter';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import qs from 'query-string';

import { usePrevious } from 'hooks';
import { reduxActions, useDispatch, useSelector } from 'store';

import Table from 'components/controls/Table';
import { PageTitle, SearchInput } from 'components/controls';

import {
  IDynamicFilter,
  IDynamicColumn,
  IDynamicAction,
  ILoadDataParam,
  IComponentParams
} from 'plugins/api/types';

import { useLoadData, useCallCommand } from 'plugins/api/endpoints';
import { loadDataUrlParser } from 'plugins/util/parser';
import {
  AdditionalAction,
  NoContextAction,
  MainAction,
  ColumnAction,
  FilterSelect,
  FilterDate,
  FilterList
} from 'plugins/components/controls';

import styles from 'theme/pages/Clients/Clients.module.scss';
import 'plugins/theme/components/TablePanel/TablePanel.scss';

// typ danych wejściowych
interface IProps {
  label: string;
  filters: IDynamicFilter[];
  actions: IDynamicAction[];
  columns: IDynamicColumn[];
  loadDataParams?: ILoadDataParam;
  componentParams?: IComponentParams;
  pageSymbol: string;
  componentSymbol: string;
  requestParams?: object;
  isSubComponent?: boolean;
}

interface IQueryParams {
  page: number;
  limit: number;
  search_keyword?: string;
  [key: string]: any;
}

const TablePanel: FC<IProps> = ({
  filters,
  actions,
  label,
  columns,
  loadDataParams,
  componentParams,
  pageSymbol,
  componentSymbol,
  requestParams
                                }) => {
  const dispatch = useDispatch();
  const { search } = useLocation();

  // pobieranie globalnego stanu kontekstu danych
  const { contextFilters } = useSelector((state) => state.dynamicPage);

  // parametry zapytania o listę klientów
  const [queryParams, setQueryParams] = useState<IQueryParams>({
    page: 1,
    limit: 20,
    ...(filters ? mapValues(keyBy(filters, 'field_symbol'), 'default_value') : {})
  });

  const loadDataUrl = loadDataUrlParser(loadDataParams?.get_url || '', {
    ...requestParams,
    ...qs.parse(search)
  });

  const currentGetUrl = loadDataParams?.get_url;
  const prevGetUrl = usePrevious(loadDataParams?.get_url);
  const prevQueryParams = usePrevious(queryParams);

  // pobranie danych tabeli
  const {
    data: tableData,
    refetch: refetchTableData,
    isFetching: isTableDataFetching
  } = useLoadData(loadDataUrl, queryParams, {
    onSuccess: (data) => {
      dispatch(reduxActions.setContextData({ [pageSymbol]: { [componentSymbol]: data } }));
    },
    enabled: false,
    cacheTime: 1,
    keepPreviousData: false
  });

  const reorderAction = actions.find((action) => action.method === 'Reorder');

  const { mutate: callCommand, isLoading: isMutating } = useCallCommand(
    reorderAction?.method_params?.command_url || '',
    {
      onSuccess: () => {
        refetchTableData();
      }
    }
  );

  const tableDataReloading = isMutating || isTableDataFetching;

  // aktualizacja danych
  useEffect(() => {
    if (!isEqual(currentGetUrl, prevGetUrl) || !isEqual(queryParams, prevQueryParams)) {
      refetchTableData();
    }
  }, [currentGetUrl, queryParams]);

  // pobieranie danych z kontekstu i zapisywanie w lokalnych filtrach
  useEffect(() => {
    const newQueryParams = contextFilters?.[pageSymbol]?.[componentSymbol];
    if (newQueryParams) {
      setQueryParams(newQueryParams);

      return;
    }

    // ustawienie wartości początkowych
    setQueryParams({
      page: 1,
      limit: 20,
      ...(filters ? mapValues(keyBy(filters, 'field_symbol'), 'default_value') : {})
    });
  }, [contextFilters, pageSymbol, componentSymbol]);

  const handleReorder = (data: any) => {
    callCommand(data);
  };

  const renderFilters = () =>
    filters?.map((filter, i) => {
      if (filter.type === 'search') {
        return (
          <div className="filter" key={i}>
            <SearchInput
              placeholder={filter.label}
              onChange={(value) =>
                setQueryParams((prevState) => {
                  const newQueryParams = {
                    ...prevState,
                    [filter.field_symbol || 'search_keyword']: value,
                    page: 1
                  };

                  dispatch(
                    reduxActions.setContextFilters({
                      [pageSymbol]: { [componentSymbol]: newQueryParams }
                    })
                  );

                  return newQueryParams;
                })
              }
              value={queryParams[filter.field_symbol || 'search_keyword']}
            />
          </div>
        );
      }

      if (filter.type === 'select') {
        return (
          <Fragment key={i}>
            <FilterSelect
              pageSymbol={pageSymbol}
              componentSymbol={componentSymbol}
              filter={filter}
              setQueryParams={setQueryParams}
              queryParams={queryParams}
            />
          </Fragment>
        );
      }

      if (filter.type === 'datetime' || filter.type === 'date') {
        return (
          <Fragment key={i}>
            <FilterDate
              pageSymbol={pageSymbol}
              componentSymbol={componentSymbol}
              filter={filter}
              setQueryParams={setQueryParams}
              queryParams={queryParams}
              isTimePicker={filter.type === 'datetime'}
            />
          </Fragment>
        );
      }

      if (filter.type === 'list') {
        return (
          <Fragment key={i}>
            <FilterList
              pageSymbol={pageSymbol}
              componentSymbol={componentSymbol}
              filter={filter}
              setQueryParams={setQueryParams}
              queryParams={queryParams}
            />
          </Fragment>
        );
      }

      return null;
    });

  const tableColumns = useMemo(() => {
    const columnsToDisplay = columns.map((column) => ({
      title: column.label,
      align: column.align ?? undefined,
      renderCell: (item: any) => renderCell(item, column)
    }));

    const mainActions: IDynamicAction[] = [];
    const additionalActions: IDynamicAction[] = [];

    each(actions, (action) => {
      if (action.type === 'CONTEXT_ADDITIONAL') {
        additionalActions.push(action);
      }

      if (action.type === 'CONTEXT_MAIN') {
        mainActions.push(action);
      }
    });

    const actionsToDisplay = {
      title: <Trans>Akcje</Trans>,
      renderCell: (item: any) => {
        return (
          <div style={{ display: 'flex' }}>
            {renderMainActionsCell(item, mainActions)}
            {!!additionalActions.length && renderAdditionalActionsCell(item, additionalActions)}
          </div>
        );
      }
    };

    if (mainActions.length === 0 && additionalActions.length === 0) {
      return columnsToDisplay;
    }

    return [...columnsToDisplay, actionsToDisplay];
  }, [columns, actions]);

  const renderMainActionsCell = (item: any, mainActions: IDynamicAction[]) => {
    return mainActions.map((action, i) => (
      <MainAction key={i} action={action} refetch={refetchTableData} item={item} />
    ));
  };

  const renderAdditionalActionsCell = (item: any, additionalActions: IDynamicAction[]) => {
    return (
      <AdditionalAction
        additionalActions={additionalActions}
        item={item}
        refetchTableData={refetchTableData}
        requestParams={requestParams}
      />
    );
  };

  // komórki tabel
  const renderCell = (item: any, column: IDynamicColumn) => {
    if (column.type === 'text' || column.type === 'string') {
      return renderTextCell(item, column);
    }

    if (column.type === 'image') {
      return renderImageCell(item, column);
    }

    if (column.type === 'address') {
      return `${item.address?.street}, ${item.address?.postal_code} ${item.address?.city}`;
    }

    if (column.type === 'boolean') {
      return item?.[column.field_symbol] ? <Check2 /> : <X />;
    }

    return null;
  };

  const renderTextCell = (item: any, column: IDynamicColumn) => {
    const columnAction = actions?.find(
      (action) => action.type === 'COLUMN' && action.column_field_symbol === column.field_symbol
    );

    if (columnAction?.column_field_symbol === column.field_symbol) {
      return (
        <div className="link">
          <ColumnAction action={columnAction} item={item} refetch={refetchTableData} />
        </div>
      );
    }

    return <span>{item?.[column.field_symbol]}</span>;
  };

  const renderImageCell = (item: any, column: IDynamicColumn) => {
    return (
      <div>
        <img src={item?.[column.field_symbol]} />
      </div>
    );
  };

  const renderNoContextActions = () => {
    const noContextActions = filter(actions, (action) => action.type === 'NO_CONTEXT');

    return noContextActions.map((action, i) => (
      <NoContextAction
        key={i}
        action={action}
        refetch={refetchTableData}
        componentParams={componentParams}
      />
    ));
  };

  return (
    <div className={classnames(styles.componentWrapper, 'TablePanel', 'StylePath-TablePanel')}>
      {label &&
        (
          <PageTitle
            title={
              <>
                {label} <span className="thin">({tableData?.total_count})</span>
              </>
            }
          />
        )}

      <div className={styles.filtersWrapper}>
        <div className="filterWrapper">
          {renderFilters()}{' '}
          {!!filters?.length && (
            <button
              className="clearFilters"
              color="secondary"
              onClick={() => {
                dispatch(
                  reduxActions.setContextFilters({
                    [pageSymbol]: { [componentSymbol]: { page: 1, limit: 20 } }
                  })
                );
                setQueryParams({ page: 1, limit: 20 });
              }}>
              <Trans>Wyczyść filtry</Trans>
            </button>
          )}
        </div>
        {renderNoContextActions()}
      </div>

      <div className={`tableWrapper ${tableDataReloading ? 'isLoading' : ''}`}>
        <Table
          columns={tableColumns}
          dataSource={tableData?.items || []}
          rowKey="field_symbol"
          pagination={{
            page: queryParams.page,
            pagesCount: tableData?.total_pages || 1,
            onChange: (page) =>
              setQueryParams((prevState) => {
                const newQueryParams = { ...prevState, page };

                dispatch(
                  reduxActions.setContextFilters({
                    [pageSymbol]: { [componentSymbol]: newQueryParams }
                  })
                );

                return newQueryParams;
              })
          }}
          dragableRows={!!actions.find((action) => action.method === 'Reorder')}
          handleReorder={handleReorder}
        />
      </div>
    </div>
  );
};

export default TablePanel;
